/**
 * Reading what the server refused, and saying it in words an admin can act on.
 *
 * Two kinds of refusal live here:
 *
 *  - **Per-row rejections** from a partial batch save — named row by row, so an
 *    admin is never left re-checking a hundred rows by hand.
 *  - **Scope refusals**, which are about *which company* the request was for
 *    rather than what it contained. They are separated out because they need a
 *    different response from every other error: a `403 scope_not_permitted`
 *    must name the company that was refused, and a `409 scope_mismatch` means
 *    the view moved underneath the screen and the only safe next step is a
 *    reload. Funnelling either into a generic message is how a config write
 *    landing on the wrong company stayed invisible in the first place.
 *
 * @link ../../../../lib/api/types/notification-config for the wire shapes.
 * @link useNotificationSetup for the hook that acts on these.
 */
import { getErrorMessage } from '../../../../hooks/useFetchData';
import type { SetupEvent } from './types';

/** The backend's code for "you may not configure the company you are viewing". */
export const SCOPE_NOT_PERMITTED = 'scope_not_permitted';

/** The backend's code for "this page is claiming a company I am not scoped to". */
export const SCOPE_MISMATCH = 'scope_mismatch';

/** One row the server refused, paired with the rejection that refused it. */
export interface FailedSave {
  event: SetupEvent;
  reason: unknown;
}

/** How many rows a failure toast names before it summarises the tail. */
const MAX_NAMED_FAILURES = 3;

/**
 * What the server refused, row by row.
 *
 * A partial batch has to say exactly which rows failed and why — "some changes
 * failed" leaves an admin re-checking a hundred rows by hand — so each failure
 * is named alongside the API's own `detail` message.
 *
 * @param failures - the rejected rows, in grid order.
 * @param attempted - how many rows the save tried to write.
 * @returns a single sentence naming the first few failures.
 */
export function describeFailures(failures: FailedSave[], attempted: number): string {
  const named = failures
    .slice(0, MAX_NAMED_FAILURES)
    .map(
      (failure) =>
        `${failure.event.name}: `
        + getErrorMessage(failure.reason, 'the server rejected the change'),
    )
    .join('; ');
  const rest = failures.length - Math.min(failures.length, MAX_NAMED_FAILURES);
  return (
    `${failures.length} of ${attempted} changes failed — ${named}`
    + (rest > 0 ? ` (and ${rest} more)` : '')
  );
}

/**
 * A refusal about scope rather than content.
 *
 * `refused` is a 403: this account may not configure the company on screen, and
 * the message names it. `mismatch` is a 409: the server is scoped somewhere else
 * now, so the screen has to be re-read before anything is written.
 */
export interface ScopeFailure {
  kind: 'refused' | 'mismatch';
  message: string;
}

/** The error envelope's body, when there is one. */
const envelope = (err: unknown): Record<string, unknown> | null => {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  return typeof data === 'object' && data !== null && !Array.isArray(data)
    ? (data as Record<string, unknown>)
    : null;
};

const readText = (value: unknown): string => (typeof value === 'string' ? value : '');

/** `details` off the envelope — always an object, so lookups stay safe. */
const details = (body: Record<string, unknown>): Record<string, unknown> => {
  const found = body['details'];
  return typeof found === 'object' && found !== null && !Array.isArray(found)
    ? (found as Record<string, unknown>)
    : {};
};

/**
 * Classify an API error as a scope refusal, or `null` when it is anything else.
 *
 * Both messages are built to guarantee the thing the admin needs is present —
 * the refused company's name for a 403, the word "Reload" for a 409 — while
 * preferring the server's own sentence when it already carries it, so the two
 * halves of the fix cannot drift apart in wording.
 *
 * @param err - the rejection from any notification-config call.
 * @returns the classified failure and the message to show, or `null`.
 */
export function readScopeFailure(err: unknown): ScopeFailure | null {
  const body = envelope(err);
  if (!body) return null;
  const code = readText(body['code']);
  if (code !== SCOPE_NOT_PERMITTED && code !== SCOPE_MISMATCH) return null;

  const detail = getErrorMessage(err, '');
  const extra = details(body);

  if (code === SCOPE_NOT_PERMITTED) {
    const name = readText(extra['requested_company_name']);
    if (!name) {
      return {
        kind: 'refused',
        message:
          detail
          || 'Your account is not linked to the company selected in the top bar, '
            + 'so its notification configuration cannot be opened.',
      };
    }
    return {
      kind: 'refused',
      message: detail.includes(name)
        ? detail
        : `${name}: your account is not linked to this company, so its `
          + 'notification configuration cannot be opened.',
    };
  }

  const name = readText(extra['actual_company_name']);
  if (detail && /reload/i.test(detail)) return { kind: 'mismatch', message: detail };
  return {
    kind: 'mismatch',
    message:
      `You are now configuring ${name || 'a different company'}. `
      + 'Reload the page before saving, so the change lands where you can see it.',
  };
}

/** The first scope refusal in a batch of per-row rejections, if there is one. */
export function firstScopeFailure(failures: FailedSave[]): ScopeFailure | null {
  for (const failure of failures) {
    const scope = readScopeFailure(failure.reason);
    if (scope) return scope;
  }
  return null;
}
